import {AttrsSchema, Component, jsx} from 'panel';
import cloneDeep from 'lodash/cloneDeep';

import {IronContextRegistry} from 'common/report/context-registry';
import 'common/report/insights/charts';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartType, FlowsBookmark} from 'common/report/flows/models/types';
import {FlowsResponse} from 'common/report/funnels/models/api-types';
import {SectionItem} from 'common/components/items-menu/types';
import {DownloadMode} from 'common/report/constants';
import {FlowsSankeyChartWidgetEl} from 'common/report/flows/charts/flows-sankey-chart';
import {FlowsTopPathsChartWidgetEl} from 'common/report/flows/charts/flows-top-paths-chart';
import {TopPathsFlowStep} from 'common/report/flows/charts/flows-top-paths-chart/models/top-paths-flow-step';
import {TrackingEvent} from 'common/report/flows/tracking';
import {getDownloadMode} from 'common/report/util';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {emitTrackEvent} from 'common/util/dom';
import {Property} from 'common/query-builder/query-block/properties-menu/types';
import {SankeyNodeType} from 'common/widgets/sankey-chart/models/types';
import {SankeyNode} from 'common/widgets/sankey-chart/models/sankey-node';
import {SankeyBreakdown} from 'common/widgets/sankey-chart/models/sanky-breakdown';
import {FlowsReport} from 'common/report/flows/models/report';
import {getDataGroupName, PopoverNode} from 'common/report/flows/visualization/util';

import {SankeyNodeContextMenuAction} from './types';
import css from './index.inline.styl';

interface ChartDisplayAttrs {
  'sankey-scroll-offset': number;
  loaded: boolean;
}

interface ChartDisplayProps {
  bookmarkParams: FlowsBookmark;
  queryResults: FlowsResponse;
  customEvents: CustomEventDefinition[];
}

export type UpdateEvent = CustomEvent<{delta: Partial<FlowsBookmark>}>;

export type PopoverActionDetail = {
  action: SankeyNodeContextMenuAction;
  node: PopoverNode;
  stepLabel: string;
  isCustomEvent?: boolean;
  breakdown?: SankeyBreakdown;
  property?: Property;
};

export type PopoverActionEvent = CustomEvent<PopoverActionDetail>;

export interface ChartDisplayJsxProps {
  props: ChartDisplayProps;
  on: {
    update: (ev: UpdateEvent) => void;
    updateScrollOffset: (ev: CustomEvent<number>) => void;
    popoverAction: (ev: PopoverActionEvent) => void;
  };
}

export class FlowsChartDisplay extends Component<EmptyObj, ChartDisplayAttrs, EmptyObj, void, IronContextRegistry> {
  private _bookmarkParams: FlowsBookmark;
  private _queryResults: FlowsResponse;
  private _customEvents: CustomEventDefinition[];

  static get attrsSchema(): AttrsSchema<ChartDisplayAttrs> {
    return {
      ...super.attrsSchema,
      'sankey-scroll-offset': `number`,
      loaded: `boolean`,
    };
  }

  set bookmarkParams(params: FlowsBookmark) {
    this._bookmarkParams = params;
    this.update();
  }

  get bookmarkParams() {
    return this._bookmarkParams;
  }

  set queryResults(results: FlowsResponse) {
    this._queryResults = results;
    this.update();
  }

  get queryResults() {
    return this._queryResults;
  }

  set customEvents(events: CustomEventDefinition[]) {
    this._customEvents = events;
    this.update();
  }

  get customEvents() {
    return this._customEvents;
  }

  get config() {
    return {
      template: () => {
        const chartType = this.bookmarkParams.chartType;
        if (chartType === ChartType.Paths) {
          return (
            <div sel='.top-paths-wrapper' class={{'download-mode': this.isDownloadMode()}}>
              <FlowsTopPathsChartWidgetEl
                attrs={{loaded: this.attr(`loaded`), 'path-height': 400, elref: `topPathsChartWidget`}}
                props={{
                  paramsAndResults: {
                    params: this.bookmarkParams,
                    results: this.queryResults,
                  },
                  stepPopoverCallback: this.helpers.topPathsStepPopoverCallback,
                }}
                on={{
                  stepPopoverAction: this.helpers.onTopPathsStepPopoverAction,
                }}
              />
            </div>
          );
        } else if (chartType === ChartType.Sankey) {
          return (
            <div sel='.sankey-wrapper' class={{'download-mode': this.isDownloadMode()}}>
              <FlowsSankeyChartWidgetEl
                attrs={{
                  'scroll-offset': this.attr(`sankey-scroll-offset`),
                  loaded: this.attr(`loaded`),
                  'tooltip-enabled': true,
                  elref: `sankeyChartWidget`,
                }}
                props={{
                  paramsAndResults: {
                    params: this.bookmarkParams,
                    results: this.queryResults,
                  },
                  nodePopoverCallback: this.helpers.sankeyNodePopoverCallback,
                }}
                on={{
                  updateSteps: this.helpers.updateSteps,
                  nodePopoverAction: this.helpers.onSankeyNodePopoverAction,
                }}
              />
            </div>
          );
        }

        return null;
      },
      css,
      useShadowDom: true,
      contexts: [`urlManager` as const, `currentProject` as const],
      helpers: {
        topPathsStepPopoverCallback: (step: TopPathsFlowStep) => {
          return {
            options: [
              {value: SankeyNodeContextMenuAction.ExpandByProperty, label: `Expand by property`, icon: `segment`},
              {
                value: SankeyNodeContextMenuAction.HideEvent,
                label: `Hide event`,
                icon: `eye-hide`,
                isDisabled: step.isAnchor,
              },
            ],
            breakdownOptions: this.getBreakdownOptions(step.event),
          };
        },
        onTopPathsStepPopoverAction: (ev) => {
          const {step, action, value} = ev.detail;
          this.dispatchPopoverAction({
            action,
            node: step,
            stepLabel: step.step,
            isCustomEvent: step.isCustomEvent,
            property: value,
          });
        },

        updateSteps: (ev) => {
          const steps = cloneDeep(this.bookmarkParams.steps);

          if (ev.detail.operation === `add`) {
            this.track(TrackingEvent.ADD_STEPS, {
              'step type': ev.detail.type,
              'num steps': ev.detail.absolute - (steps[ev.detail.anchor][ev.detail.type] || 0),
            });
          } else {
            this.track(TrackingEvent.REMOVE_STEPS, {
              'step type': ev.detail.type,
              'num steps': steps[ev.detail.anchor][ev.detail.type] - ev.detail.absolute,
            });
          }

          if (ev.detail.scrollOffset) {
            this.dispatchEvent(new CustomEvent(`updateScrollOffset`, {detail: ev.detail.scrollOffset}));
          }

          if (ev.detail.absolute >= 0) {
            steps[ev.detail.anchor][ev.detail.type] = ev.detail.absolute;
            this.dispatchUpdate({steps});
          }
        },

        sankeyNodePopoverCallback: (node: SankeyNode) => {
          const report: FlowsReport = FlowsReport.fromBookmarkParams(this.bookmarkParams);
          const viewUserslabel = `View ${getDataGroupName({activeDataGroupId: report.dataGroupId, plural: true})}`;

          let options: SectionItem[] = [
            {value: SankeyNodeContextMenuAction.AddCardinality, icon: `plus`, label: `Add a row of events`},
            {
              value: SankeyNodeContextMenuAction.RemoveCardinality,
              icon: `circle-clear`,
              label: `Remove a row of events`,
              isDisabled: this.bookmarkParams.cardinality < 2,
            },
            {
              value: SankeyNodeContextMenuAction.ViewUsers,
              icon: `team`,
              label: viewUserslabel,
            },
          ];

          if (![SankeyNodeType.Dropoff, SankeyNodeType.Pruned].includes(node.type)) {
            options = [
              {value: SankeyNodeContextMenuAction.ExpandByProperty, label: `Expand by property`, icon: `segment`},
              {
                value: SankeyNodeContextMenuAction.HideEvent,
                label: `Hide event`,
                icon: `eye-hide`,
                isDisabled: node.type === SankeyNodeType.Anchor || node.isCustomEvent,
              },
              ...options,
            ];
          }

          return {options, breakdownOptions: this.getBreakdownOptions(node.event)};
        },

        onSankeyNodePopoverAction: (ev) => {
          const {action, node, breakdown, value} = ev.detail;
          const stepLabel = (node as SankeyNode).letterLabel;
          this.dispatchPopoverAction({
            action,
            node,
            stepLabel,
            isCustomEvent: node.isCustomEvent,
            breakdown,
            property: value,
          });
        },
      },
    };
  }

  isDownloadMode() {
    return getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None;
  }

  getBreakdownOptions(eventName: string) {
    return {
      disabledItems: this.bookmarkParams.segments
        .filter((segment) => segment.event.value === eventName)
        .map((segment) => segment.property.value),
    };
  }

  // popover actions need to be handled by parent app to be able to implement "undo" toasts
  dispatchPopoverAction(detail: PopoverActionDetail) {
    const event: PopoverActionEvent = new CustomEvent(`popoverAction`, {detail});
    this.dispatchEvent(event);
  }

  dispatchUpdate(delta: Partial<FlowsBookmark>) {
    const event: UpdateEvent = new CustomEvent(`update`, {detail: {delta}});
    this.dispatchEvent(event);
  }

  track(eventName: string, props?: {[key: string]: any}) {
    emitTrackEvent(this, {eventName, props: props || {}});
  }
}

export const FlowsChartDisplayEl = defineAndMakeJsxFactory<ChartDisplayJsxProps>(
  `flows-chart-display`,
  FlowsChartDisplay,
);
export const FlowsChartDisplayReact = panel2react<ChartDisplayJsxProps>(`flows-chart-display`, FlowsChartDisplay);
